import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { fetchCrops } from '../../features/crops/cropSlice';
import CropCard from './CropCard';
import AddCropForm from './AddCropForm';
import VarietiesList from './varieties/VarietiesList';
import Button from '../ui/Button';
import SearchInput from '../ui/SearchInput';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import { Crop } from '../../types/crops';

const CropList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { crops, loading, error } = useSelector((state: RootState) => state.crops);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showVarieties, setShowVarieties] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<Crop['status'] | 'all'>('all');

  useEffect(() => {
    dispatch(fetchCrops());
  }, [dispatch]);

  const filteredCrops = crops.filter(crop => {
    const matchesSearch = 
      crop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      crop.variety?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || crop.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">{t('crops.title')}</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            onClick={() => setShowVarieties(true)}
            variant="secondary"
            className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-700 border border-gray-300"
          >
            {t('crops.varieties.title')}
          </Button>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
          >
            {showAddForm ? t('common.cancel') : t('crops.addCrop')}
          </Button>
        </div>
      </div>

      {showAddForm && (
        <AddCropForm onSuccess={() => setShowAddForm(false)} />
      )}

      {showVarieties && (
        <VarietiesList onClose={() => setShowVarieties(false)} />
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('crops.searchPlaceholder')}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Crop['status'] | 'all')}
          className="block w-full sm:w-48 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          aria-label={t('common.status')}
        >
          <option value="all">{t('common.allStatus')}</option>
          <option value="active">{t('crops.status.active')}</option>
          <option value="harvested">{t('crops.status.harvested')}</option>
          <option value="failed">{t('crops.status.failed')}</option>
          <option value="pending">{t('crops.status.pending')}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCrops.length === 0 ? (
          <p className="text-gray-500 col-span-full text-center py-8">
            {t('crops.noCrops')}
          </p>
        ) : (
          filteredCrops.map((crop) => (
            <CropCard key={crop.id} crop={crop} />
          ))
        )}
      </div>
    </div>
  );
};

export default CropList;